import { Box, Text } from '@factorialco/deprecated-design-system'
import { IncidencePersonalHiringDetailsFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import type IncidenceDetail from 'modules/bookkeepers/store/models/IncidenceDetail'
import {
  activityTypes,
  employmentTypes,
  flatRateTaxes,
  personalKeyGroups,
} from 'modules/contracts/hooks/useFetchGermanContractData'
import {
  getActivityType,
  getEmploymentType,
  getFlatRateTax,
  getPersonalKeyGroup,
} from 'modules/contracts/lib/germanCountrySpecifics'
import { getIncidenceDetailsLabel } from 'modules/payroll_bundle/lib/getIncidenceDetailsLabel'
import { FieldObject, IncidenceDetailsField, ValueModifier } from 'modules/payroll_bundle/lib/types'
import { hasValidValue } from 'modules/payroll_bundle/lib/utils'

import DetailLine from '../_DetailLine'

interface Props {
  title: string
  incidenceDetailModel: IncidenceDetail
  hiringFields: IncidenceDetailsField[]
  personalHiringDetails?: IncidencePersonalHiringDetailsFragment[]
  legalEntityId?: number
}

const modifyValue = (fieldObject: FieldObject, valueModifier?: ValueModifier) => {
  if (valueModifier) {
    return valueModifier(fieldObject) ?? ''
  }

  return fieldObject.fieldValue as string
}

const HiringDetails = ({
  incidenceDetailModel,
  title,
  hiringFields,
  personalHiringDetails,
  legalEntityId,
}: Props) => {
  const company = companies.current

  const processFieldValue = ({ field, extraField, valueModifier }: IncidenceDetailsField) => {
    const fieldObject: FieldObject = {
      fieldValue: incidenceDetailModel.attributes[field],
      extraValue: incidenceDetailModel.attributes[extraField ?? ''],
      company,
      legalEntityId,
    }

    if (field === 'de_activity_type') {
      const activityTypeId = incidenceDetailModel.attributes[field]
      const activityType = getActivityType(activityTypes(), activityTypeId)
      if (activityType) {
        return activityType.label
      }
    }
    if (field === 'de_employment_type') {
      const employmentTypeId = incidenceDetailModel.attributes[field]
      const employmentType = getEmploymentType(employmentTypes(), employmentTypeId)
      if (employmentType) {
        return employmentType.label
      }
    }
    if (field === 'de_flat_rate_tax') {
      const flatRateTaxId = incidenceDetailModel.attributes[field]
      const flatRateTax = getFlatRateTax(flatRateTaxes(), flatRateTaxId)
      if (flatRateTax) {
        return flatRateTax.label
      }
    }
    if (field === 'de_personal_key_group_id') {
      const personalKeyGroupId = incidenceDetailModel.attributes[field]
      const personalKeyGroup = getPersonalKeyGroup(personalKeyGroups(), personalKeyGroupId)
      if (personalKeyGroup) {
        return personalKeyGroup.label
      }
    }

    return modifyValue(fieldObject, valueModifier)
  }

  return (
    <Box gap="s8" paddingBottom="s24">
      <Text.Normal size="200" color="grey800" weight="bold">
        {title}
      </Text.Normal>
      <Box gap="s2">
        <Box gap="s2" component="ul" paddingLeft="s16" style={{ listStyle: 'disc' }}>
          {hiringFields.map(
            (hiringField) =>
              hasValidValue(incidenceDetailModel.attributes[hiringField.field]) && (
                <DetailLine
                  key={hiringField.field}
                  component="li"
                  title={getIncidenceDetailsLabel(hiringField.translationKey)}
                  value={processFieldValue(hiringField)}
                  style={{ display: 'list-item' }}
                />
              )
          )}
        </Box>
        {personalHiringDetails &&
          personalHiringDetails.map((personalHiringDetail) => (
            <div
              key={`${personalHiringDetail.legalEntity?.legalName}-${personalHiringDetail.employeeGroup?.name}`}
            >
              <Box gap="s2" component="ul" paddingLeft="s16" style={{ listStyle: 'disc' }}>
                <DetailLine
                  component="li"
                  title={i18n.t('bookkeepers.incidence.details.legal_entity')}
                  value={personalHiringDetail.legalEntity?.legalName ?? ''}
                  style={{ display: 'list-item' }}
                />
              </Box>
              <Box gap="s2" component="ul" paddingLeft="s16" style={{ listStyle: 'disc' }}>
                <DetailLine
                  component="li"
                  title={i18n.t('bookkeepers.incidence.details.employee_group')}
                  value={personalHiringDetail.employeeGroup?.name ?? ''}
                  style={{ display: 'list-item' }}
                />
              </Box>
            </div>
          ))}
      </Box>
    </Box>
  )
}

export default HiringDetails
